import { useEffect, useMemo } from "react";
import { useBalanceSheets, IBalanceSheet } from "api/accountingService";

interface Props {
  searchText: string;
  setSubtitle: (s: string) => void;
}

const fmt = (v: number): string =>
  v || v === 0 ? v.toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "";

// Kolejność prezentacji: miesiące, roczny na końcu (Month=0 → 13).
const sortKey = (b: IBalanceSheet) => b.year * 100 + (b.month === 0 ? 13 : b.month);

const BilanseRegister = ({ searchText, setSubtitle }: Props) => {
  const { data: sheets = [], isLoading } = useBalanceSheets();

  const rows = useMemo(() => {
    const sorted = [...sheets].sort((a, b) => sortKey(a) - sortKey(b));
    const q = searchText.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter((b) => `${b.label} ${b.year}`.toLowerCase().includes(q));
  }, [sheets, searchText]);

  useEffect(() => {
    const years = [...new Set(sheets.map((s) => s.year))].sort();
    setSubtitle(years.length ? `Bilanse · ${years.join(", ")}` : "Bilanse");
  }, [sheets, setSubtitle]);

  const cell: React.CSSProperties = { padding: "6px 10px", borderBottom: "1px solid #eee", whiteSpace: "nowrap" };
  const num: React.CSSProperties = { ...cell, textAlign: "right" };
  const head: React.CSSProperties = {
    padding: "8px 10px",
    borderBottom: "2px solid #e5e0ec",
    color: "#79767a",
    fontWeight: 600,
    fontSize: 12,
    position: "sticky",
    top: 0,
    background: "#fdf8fd",
    whiteSpace: "nowrap",
  };
  const headNum: React.CSSProperties = { ...head, textAlign: "right" };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: "#fdf8fd" }}>
      <div style={{ flex: 1, overflow: "auto", padding: "12px 16px 16px" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr>
              <th style={{ ...head, textAlign: "left" }}>Okres</th>
              <th style={headNum}>Aktywa trwałe</th>
              <th style={headNum}>Aktywa obrotowe</th>
              <th style={headNum}>Suma aktywów</th>
              <th style={headNum}>Kapitał (fundusz) własny</th>
              <th style={headNum}>Zobowiązania i rezerwy</th>
              <th style={headNum}>Suma pasywów</th>
              <th style={headNum}>Wynik finansowy</th>
              <th style={headNum}>Kontrola (A − P)</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td style={cell} colSpan={9}>Ładowanie…</td></tr>
            ) : rows.length === 0 ? (
              <tr>
                <td style={{ ...cell, color: "#aaa", fontStyle: "italic" }} colSpan={9}>
                  Brak bilansów — wczytaj plik w Ustawienia → Budżet i FK → Migracja (kafelek „BO/ZOiS").
                </td>
              </tr>
            ) : (
              rows.map((b) => {
                const control = b.totalAssets - b.totalLiabilities;
                const isAnnual = b.periodType === "Annual";
                const period = b.label.includes(String(b.year)) ? b.label : `${b.label} ${b.year}`;
                return (
                  <tr key={b.id ?? `${b.year}-${b.month}`} style={isAnnual ? { fontWeight: 600, background: "#f3f0f8" } : undefined}>
                    <td style={{ ...cell, fontWeight: isAnnual ? 700 : 400 }}>{period}</td>
                    <td style={num}>{fmt(b.fixedAssets)}</td>
                    <td style={num}>{fmt(b.currentAssets)}</td>
                    <td style={num}>{fmt(b.totalAssets)}</td>
                    <td style={num}>{fmt(b.equity)}</td>
                    <td style={num}>{fmt(b.liabilities)}</td>
                    <td style={num}>{fmt(b.totalLiabilities)}</td>
                    <td style={num}>{fmt(b.netResult)}</td>
                    <td style={{ ...num, color: Math.abs(control) < 0.01 ? "#2e7d32" : "#c62828" }}>
                      {fmt(control)}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BilanseRegister;
